import { AddDeviceInput } from "../../API";

// Generic device template type that supports both SKU flow (no nodeNumber)
// and legacy Device Template flow (with nodeNumber)
export interface DeviceTemplateData {
  deviceType: string;
  deviceIp?: string; // Now provided at onboarding time for SKU flow
  nodeNumber?: number; // Optional for SKU flow, required for legacy templates
  model: string[];
  password?: string;
  isPT?: boolean;
  panTiltType?: string;
  panTiltIP?: string; // Separate PT IP (if different from device IP)
  hasRecordVideo?: boolean;
}

export enum DeviceTypeEnum {
  CH4 = "CC CH4",
  FB4 = "CC FB4",
  FB4_THERMAL = "CC FB4 Thermal",
  V1 = "V1",
}
interface SelectedLocation {
  value: string;
  title: string;
}

interface SelectedNode {
  nodeId: string;
  nodeName: string;
  nodeIP: string;
}

interface MapTemplateOptions {
  templateData: DeviceTemplateData[];
  selectedCustomerId: string;
  selectedLocation: SelectedLocation | null;
  selectedNodes: SelectedNode[];
  devicePassword: string;
  skuNumber?: string;
  configurationName?: string;
  serialNumber?: string;
  componentSerialNumbers?: string[];
}

export const mapTemplateDataToDeviceInput = ({
  templateData,
  selectedCustomerId,
  selectedLocation,
  selectedNodes,
  devicePassword,
  skuNumber,
  configurationName,
  serialNumber,
  componentSerialNumbers,
}: MapTemplateOptions): AddDeviceInput[] => {
  const devices: AddDeviceInput[] = [];
  let ogiCount = 1;
  let v1Count = 1;
  let fb4Count = 1;
  let fb4RTSPCount = 1;
  let fb4ThermalCount = 1;
  let fb4ThermalRTSPCount = 1;

  templateData.forEach((template, index) => {
    const {
      deviceType,
      deviceIp = "",
      nodeNumber,
      isPT,
      panTiltType,
      panTiltIP,
      hasRecordVideo,
    } = template;

    // Use provided panTiltIP, or fall back to deviceIp for integrated PT
    const effectivePanTiltIP = panTiltIP || deviceIp;

    const locationTitle = selectedLocation?.title?.replace(/\s+/g, "") || "";

    // Device names use location-based prefix only.
    // SKU/product/serial info is stored as separate fields, not embedded in the name.
    const locationId = selectedLocation?.value ?? "";

    // For SKU flow: selectedNodes array has one entry per device (direct index mapping)
    // For legacy template flow (has nodeNumber): use nodeNumber - 1 as index
    const nodeIndex = nodeNumber !== undefined ? nodeNumber - 1 : index;
    const selectedNode = selectedNodes[nodeIndex];
    // nodeId from filteredNodes already uses node.id which is in correct # format
    const nodeId = selectedNode?.nodeId ?? "";
    const nodeName = selectedNode?.nodeName ?? "";
    const nodeIp = selectedNode?.nodeIP ?? "";

    // Get component serial number for this device (if provided)
    const componentSerialNumber = componentSerialNumbers?.[index] || undefined;

    const baseDevice = {
      customerId: selectedCustomerId,
      locationId,
      locationName: selectedLocation?.title,
      nodeId,
      nodeName,
      makeModelId: deviceType,
      cameraIpAddress: deviceIp,
      ...(skuNumber && { skuNumber }),
      ...(configurationName && { configurationName }),
      ...(serialNumber && { serialNumber }),
      ...(componentSerialNumber && { componentSerialNumber }),
      ...((skuNumber || serialNumber) && {
        onboardedAt: Date.now(),
      }),
    };

    const ch4SourceVideo = `rtsp://${devicePassword}@${deviceIp}/live1`;
    const v1SourceVideo = `rtsp://${deviceIp}:554/live?channel=0&subtype=0`;
    const fb4SourceVideo = `rtsp://${devicePassword}@${deviceIp}/cam/realmonitor?channel=1&subtype=1`;
    const fb4ThermSourceVideo = `rtsp://${devicePassword}@${deviceIp}/cam/realmonitor?channel=2&subtype=1`;

    if (deviceType === DeviceTypeEnum.CH4) {
      // Use template's isPT value, or default to true for CH4
      const hasPanTilt = isPT !== undefined ? isPT : true;
      const count = ogiCount++;

      devices.push({
        ...baseDevice,
        name: `${locationTitle}-ch4-${count}`,
        deviceData: JSON.stringify({
          hasPanTilt,
          ...(hasPanTilt && {
            panTiltIP: effectivePanTiltIP,
            panTiltType: panTiltType || "",
          }),
          recentFrame: "",
          keyFrame: "",
          rtspHost: `rtsp://${nodeIp}/${locationTitle}-ch4`,
          sourceVideo: ch4SourceVideo,
          hasZoom: true,
          protocol: "",
          hasRecordVideo: hasRecordVideo ?? true,
        }),
      });
    } else if (deviceType === DeviceTypeEnum.V1) {
      // Use template's isPT value, or default to true for V1
      const hasPanTilt = isPT !== undefined ? isPT : true;
      const count = v1Count++;

      devices.push({
        ...baseDevice,
        name: `${locationTitle}-multi-${count}`,
        deviceData: JSON.stringify({
          hasPanTilt,
          ...(hasPanTilt && {
            panTiltIP: effectivePanTiltIP,
            panTiltType: panTiltType || "",
          }),
          recentFrame: "",
          keyFrame: "",
          rtspHost: `rtsp://${nodeIp}/${locationTitle}-multi`,
          sourceVideo: v1SourceVideo,
          hasZoom: true,
          protocol: "",
          hasRecordVideo: true,
        }),
      });
    } else if (deviceType === DeviceTypeEnum.FB4) {
      // Use template's isPT value, or default to false for FB4
      const hasPanTilt = isPT !== undefined ? isPT : false;
      const count = fb4Count++;

      devices.push({
        ...baseDevice,
        name: `${locationTitle}-fb4-${count}`,
        deviceData: JSON.stringify({
          hasPanTilt,
          ...(hasPanTilt && {
            panTiltIP: effectivePanTiltIP,
            panTiltType: panTiltType || "",
          }),
          recentFrame: "",
          keyFrame: "",
          rtspHost: `rtsp://${nodeIp}/${locationTitle}-fb4-${fb4RTSPCount++}`,
          sourceVideo: fb4SourceVideo,
          hasZoom: false,
          protocol: "",
          hasRecordVideo: true,
        }),
      });
    } else if (deviceType === DeviceTypeEnum.FB4_THERMAL) {
      // Use template's isPT value, or default to false for FB4 Thermal
      const hasPanTilt = isPT !== undefined ? isPT : false;
      const count = fb4ThermalCount++;

      devices.push({
        ...baseDevice,
        name: `${locationTitle}-fb4therm-${count}`,
        deviceData: JSON.stringify({
          hasPanTilt,
          ...(hasPanTilt && {
            panTiltIP: effectivePanTiltIP,
            panTiltType: panTiltType || "",
          }),
          recentFrame: "",
          keyFrame: "",
          rtspHost: `rtsp://${nodeIp}/${locationTitle}-fb4therm-${fb4ThermalRTSPCount++}`,
          sourceVideo: fb4ThermSourceVideo,
          hasZoom: false,
          protocol: "",
          hasRecordVideo: true,
        }),
      });
    } else {
      const deviceTypeName = deviceType.replace(/\s+/g, "").toLowerCase();

      devices.push({
        ...baseDevice,
        name: `${locationTitle}-${deviceTypeName}`,
        deviceData: JSON.stringify({}),
      });
    }
  });

  return devices;
};
